import apiClient from './api';

/** Registered Devices - the staff half of the enrolment handshake; the device half is called by the apps. */
const base = (restaurantId, id) =>
  `/users/restaurant/${restaurantId}/devices/${id ? `${id}/` : ''}`;

export const deviceService = {
  /** All devices, or one status bucket. Returns {results, counts, ...}. */
  list: async (restaurantId, { status } = {}) => {
    const { data } = await apiClient.get(base(restaurantId), {
      params: status ? { status } : {},
    });
    return data || { results: [], counts: {} };
  },

  /** Approve → the device is issued its token on its next status poll. */
  approve: async (restaurantId, id, { name, deviceType, locationId } = {}) => {
    const { data } = await apiClient.post(`${base(restaurantId, id)}approve/`, {
      name,
      device_type: deviceType,
      location_id: locationId || null,
    });
    return data?.data;
  },

  /** Deny a request -> DENIED, distinct from revoke, with the row kept so it can't quietly return as fresh. */
  deny: async (restaurantId, id, reason = '') => {
    const { data } = await apiClient.post(`${base(restaurantId, id)}deny/`, { reason });
    return data?.data;
  },

  /** De-register → token destroyed, next connection refused. */
  revoke: async (restaurantId, id, reason = '') => {
    const { data } = await apiClient.post(`${base(restaurantId, id)}revoke/`, { reason });
    return data?.data;
  },

  /** Audit trail, newest first; by deviceId for one machine, or by fingerprint to follow it across registrations. */
  audit: async (restaurantId, { deviceId, fingerprint } = {}) => {
    const params = {};
    if (deviceId) params.device = deviceId;
    if (fingerprint) params.fingerprint = fingerprint;
    const { data } = await apiClient.get(`${base(restaurantId)}audit/`, { params });
    return data?.results || [];
  },

  /** Who worked this terminal and for how long, by device id, since a shift belongs to its registration. */
  sessions: async (restaurantId, deviceId, { days = 30 } = {}) => {
    const { data } = await apiClient.get(`${base(restaurantId, deviceId)}sessions/`, {
      params: { days },
    });
    // `device` rides along so the full-page report can open from a bare URL without a second request.
    return { rows: data?.results || [], summary: data?.summary || null, device: data?.device || null };
  },

  update: async (restaurantId, id, { name, deviceType, locationId } = {}) => {
    const body = {};
    if (name !== undefined) body.name = name;
    if (deviceType !== undefined) body.device_type = deviceType;
    if (locationId !== undefined) body.location_id = locationId || null;
    const { data } = await apiClient.patch(base(restaurantId, id), body);
    return data?.data;
  },

  /** Forget the registration entirely; the machine can enrol again fresh. */
  remove: async (restaurantId, id) => {
    const { data } = await apiClient.delete(base(restaurantId, id));
    return data?.success;
  },

  /* ---- pairing codes -----------------------------------------------------
     How a machine is SET UP, before anybody signs in on it: a manager mints a
     code against a branch, reads it out to whoever is installing the terminal,
     and the exe's setup screen redeems it. The code carries the branch, so the
     machine comes back registered and pointed at the right one. */

  /** The recent codes, live ones first in practice (newest first). */
  pairingCodes: async (restaurantId) => {
    const { data } = await apiClient.get(`${base(restaurantId)}pairing-codes/`);
    return data?.results || [];
  },

  /** Mint one. `deviceType` blank accepts any machine; `minutes` is how long it lives. */
  createPairingCode: async (restaurantId, { locationId, deviceType = '', name = '', minutes } = {}) => {
    const { data } = await apiClient.post(`${base(restaurantId)}pairing-codes/`, {
      location_id: locationId ? Number(locationId) : null,
      device_type: deviceType || '',
      name,
      minutes,
    });
    return data?.data;
  },

  /** Take one back before it is used. */
  cancelPairingCode: async (restaurantId, codeId) => {
    const { data } = await apiClient.delete(`${base(restaurantId)}pairing-codes/${codeId}/`);
    return data?.data;
  },

  /** Set the password opening that terminal's Settings; write-only, since a forgotten one is replaced. */
  setSettingsPassword: async (restaurantId, id, password) => {
    const { data } = await apiClient.post(
      `${base(restaurantId, id)}settings-password/`, { password });
    return data?.data;
  },

  /** Clear it. The terminal then refuses to open Settings at all. */
  clearSettingsPassword: async (restaurantId, id) => {
    const { data } = await apiClient.delete(`${base(restaurantId, id)}settings-password/`);
    return data?.data;
  },
};

export default deviceService;
